import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { RefreshCw, Search, Star } from 'lucide-react';
import { API, toast } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatBadgeLabel, formatUiLabel } from '@/utils/displayLabels';

const PAGE_SIZE = 25;

const planOptions = [
  { value: 'all', label: 'Tous les vendeurs' },
  { value: 'pro', label: 'Vendeurs Pro' },
  { value: 'free', label: 'Vendeurs gratuits' },
];

const kycOptions = [
  { value: 'all', label: 'KYC: tous' },
  { value: 'approved', label: 'KYC validé' },
  { value: 'pending', label: 'KYC en attente' },
  { value: 'rejected', label: 'KYC rejeté' },
  { value: 'none', label: 'KYC non soumis' },
];

const statusOptions = [
  { value: 'all', label: 'Statut: tous' },
  { value: 'active', label: 'Actifs' },
  { value: 'suspended', label: 'Suspendus' },
  { value: 'archived', label: 'Archivés' },
];

const getAccountStatusBadge = (status) => {
  if (status === 'active') {
    return <Badge className="bg-green-100 text-green-700">Actif</Badge>;
  }
  if (status === 'suspended') {
    return <Badge className="bg-amber-100 text-amber-700">Suspendu</Badge>;
  }
  if (status === 'archived') {
    return <Badge className="bg-gray-100 text-gray-700">Archivé</Badge>;
  }
  return <Badge variant="outline">{status || '-'}</Badge>;
};

const getKycBadge = (kycStatus) => {
  if (kycStatus === 'approved') {
    return <Badge className="bg-green-100 text-green-700">Validé</Badge>;
  }
  if (kycStatus === 'pending') {
    return <Badge className="bg-amber-100 text-amber-700">En attente</Badge>;
  }
  if (kycStatus === 'rejected') {
    return <Badge className="bg-red-100 text-red-700">Rejeté</Badge>;
  }
  return <Badge variant="outline">Non soumis</Badge>;
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('fr-FR');
};

const VendorsManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: PAGE_SIZE, total: 0, total_pages: 1 });
  const [filters, setFilters] = useState({
    search: '',
    planFilter: 'all',
    kycFilter: 'all',
    statusFilter: 'all',
    page: 1,
  });

  const loadVendors = useCallback(async (silent = false) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const response = await axios.get(`${API}/admin/vendors`, {
        params: {
          page: filters.page,
          page_size: PAGE_SIZE,
          search: filters.search || undefined,
          plan_filter: filters.planFilter,
          kyc_filter: filters.kycFilter,
          status_filter: filters.statusFilter,
        },
      });
      const payload = response.data || {};
      setVendors(payload.items || []);
      setPagination(payload.pagination || { page: 1, page_size: PAGE_SIZE, total: 0, total_pages: 1 });
    } catch (error) {
      const message = error.response?.data?.detail || 'Impossible de charger les vendeurs';
      toast.error(typeof message === 'string' ? message : 'Impossible de charger les vendeurs');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters.page, filters.planFilter, filters.kycFilter, filters.search, filters.statusFilter]);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  const handleToggleActive = async (vendor) => {
    const nextActive = !(vendor?.is_active ?? true);
    try {
      await axios.patch(`${API}/admin/users/${vendor.user_id}/toggle-active`, null, {
        params: { is_active: nextActive },
      });
      toast.success(nextActive ? 'Vendeur activé' : 'Vendeur suspendu');
      loadVendors(true);
    } catch (error) {
      const message = error.response?.data?.detail || 'Action impossible';
      toast.error(typeof message === 'string' ? message : 'Action impossible');
    }
  };

  const handleValidate = async (vendor) => {
    try {
      await axios.patch(`${API}/admin/users/${vendor.user_id}/validate`, null, {
        params: { is_validated: true },
      });
      toast.success('Vendeur validé');
      loadVendors(true);
    } catch (error) {
      const message = error.response?.data?.detail || 'Validation impossible';
      toast.error(typeof message === 'string' ? message : 'Validation impossible');
    }
  };

  const pageSummary = useMemo(() => {
    const total = Number(pagination.total || 0);
    const proCount = vendors.filter((vendor) => vendor.is_pro_active).length;
    const kycPending = vendors.filter((vendor) => vendor.kyc_status === 'pending').length;
    const suspendedCount = vendors.filter((vendor) => vendor.status === 'suspended').length;
    return { total, proCount, kycPending, suspendedCount };
  }, [pagination.total, vendors]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Vendeurs</h2>
          <p className="text-sm text-gray-500">Vue admin consolidée des vendeurs, abonnements, KYC et portefeuille.</p>
        </div>
        <Button
          variant="outline"
          className="gap-2"
          onClick={() => loadVendors(true)}
          disabled={refreshing}
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total vendeurs</CardDescription>
            <CardTitle>{pageSummary.total}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Pro (page courante)</CardDescription>
            <CardTitle>{pageSummary.proCount}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>KYC en attente</CardDescription>
            <CardTitle>{pageSummary.kycPending}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Suspendus</CardDescription>
            <CardTitle>{pageSummary.suspendedCount}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtres</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-5">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              className="pl-9"
              placeholder="Nom, email, téléphone, boutique..."
              value={filters.search}
              onChange={(event) => {
                setFilters((current) => ({ ...current, search: event.target.value, page: 1 }));
              }}
            />
          </div>
          <Select
            value={filters.planFilter}
            onValueChange={(value) => setFilters((current) => ({ ...current, planFilter: value, page: 1 }))}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {planOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.kycFilter}
            onValueChange={(value) => setFilters((current) => ({ ...current, kycFilter: value, page: 1 }))}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {kycOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.statusFilter}
            onValueChange={(value) => setFilters((current) => ({ ...current, statusFilter: value, page: 1 }))}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {statusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Liste vendeurs</CardTitle>
          <CardDescription>{pagination.total} vendeur(s) correspondant aux filtres</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-sm text-gray-500">Chargement des vendeurs...</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vendeur</TableHead>
                    <TableHead>Boutique</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Abonnement</TableHead>
                    <TableHead>KYC</TableHead>
                    <TableHead>Livraisons</TableHead>
                    <TableHead>Portefeuille</TableHead>
                    <TableHead>Avis</TableHead>
                    <TableHead>Badges</TableHead>
                    <TableHead>Créé le</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vendors.map((vendor) => (
                    <TableRow key={vendor.vendor_id}>
                      <TableCell>
                        <div className="font-medium">{vendor.full_name || '-'}</div>
                        <div className="text-xs text-gray-500">{vendor.email || '-'}</div>
                        <div className="text-xs text-gray-500">{vendor.phone || '-'}</div>
                        <div className="text-xs text-gray-500">{vendor.city || '-'}</div>
                      </TableCell>
                      <TableCell>{vendor.organization_name || '-'}</TableCell>
                      <TableCell>{getAccountStatusBadge(vendor.status)}</TableCell>
                      <TableCell>
                        <Badge className={vendor.email_verified ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                          {vendor.email_verified ? 'Verifie' : 'A verifier'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <Badge className={vendor.is_pro_active ? 'bg-cyan-100 text-cyan-700' : 'bg-gray-100 text-gray-700'}>
                            {vendor.is_pro_active ? 'Pro' : 'Gratuit'}
                          </Badge>
                          <div className="text-xs text-gray-500">{formatUiLabel(vendor.subscription_plan || 'free')}</div>
                        </div>
                      </TableCell>
                      <TableCell>{getKycBadge(vendor.kyc_status)}</TableCell>
                      <TableCell>{vendor.deliveries_count || 0}</TableCell>
                      <TableCell>
                        <div className="font-medium">{Number(vendor.wallet_balance || 0).toLocaleString()}</div>
                        <div className="text-xs text-gray-500">
                          {vendor.wallet_currency || 'FCFA'} · {formatUiLabel(vendor.wallet_status || '-')}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm">
                          <Star className="h-4 w-4 text-amber-500" />
                          <span>{Number(vendor.average_rating || 0).toFixed(2)} / 5</span>
                        </div>
                        <div className="text-xs text-gray-500">{vendor.total_reviews || 0} avis</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(vendor.badges || []).slice(0, 3).map((badge) => (
                            <Badge key={badge} variant="outline" className="text-[10px] uppercase">{formatBadgeLabel(badge)}</Badge>
                          ))}
                          {(vendor.badges || []).length > 3 && (
                            <Badge variant="outline" className="text-[10px]">+{(vendor.badges || []).length - 3}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{formatDate(vendor.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          {!vendor.is_validated && (
                            <Button size="sm" variant="outline" onClick={() => handleValidate(vendor)}>
                              Valider
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => handleToggleActive(vendor)}>
                            {vendor.is_active ? 'Suspendre' : 'Activer'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => navigate('/admin/subscriptions')}>
                            Abonnement
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => navigate('/admin/wallets')}>
                            Portefeuille
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => navigate('/admin/kyc')}>
                            KYC
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(vendor.review_link_path || `/vendors/${vendor.organization_id || vendor.vendor_id}/review`, '_blank', 'noopener,noreferrer')}
                          >
                            Avis
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {vendors.length === 0 && (
                <div className="py-8 text-center text-sm text-gray-500">
                  Aucun vendeur trouvé pour les filtres actuels.
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
          Page {pagination.page} / {pagination.total_pages}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={filters.page <= 1 || loading}
            onClick={() => setFilters((current) => ({ ...current, page: Math.max(1, current.page - 1) }))}
          >
            Précédent
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={filters.page >= Number(pagination.total_pages || 1) || loading}
            onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}
          >
            Suivant
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VendorsManagement;

